/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */

/* 
  code from:
  https://github.com/mantinedev/mantine/blob/master/packages/%40mantine/hooks/src/use-local-storage/create-storage.ts
*/
import { useCallback, useEffect, useState } from 'react';

export type StorageType = 'localStorage' | 'sessionStorage';

export interface UseStorageOptions<T> {
  /** Storage key */
  key: string;

  /** Default value that will be set if value is not found in storage */
  defaultValue?: T;

  /** If set to true, value will be updated in useEffect after mount. Default value is true. */
  getInitialValueInEffect?: boolean;

  /** Determines whether the value must be synced between browser tabs, `true` by default */
  sync?: boolean;

  /** Function to serialize value into string to be save in storage */
  serialize?: (value: T) => string;

  /** Function to deserialize string value from storage to value */
  deserialize?: (value: string | undefined) => T;
}

function serializeJSON<T>(value: T) {
  try {
    return JSON.stringify(value);
  } catch (error) {
    throw new Error(`useLocalStorage: Failed to serialize the value`, { cause: error });
  }
}

function deserializeJSON(value: string | undefined) {
  try {
    return value && JSON.parse(value);
  } catch (error) {
    console.warn('useLocalStorage: Failed to deserialize the value', { cause: error });
    return value;
  }
}

function createStorageHandler(type: StorageType) {
  const getItem = (key: string) => {
    try {
      return window[type].getItem(key);
    } catch (error) {
      console.warn('useLocalStorage: Failed to get value from storage, localStorage is blocked', {
        cause: error,
      });
      return null;
    }
  };

  const setItem = (key: string, value: string) => {
    try {
      window[type].setItem(key, value);
    } catch (error) {
      console.warn('useLocalStorage: Failed to set value to storage, localStorage is blocked', {
        cause: error,
      });
    }
  };

  const removeItem = (key: string) => {
    try {
      window[type].removeItem(key);
    } catch (error) {
      console.warn(
        'useLocalStorage: Failed to remove value from storage, localStorage is blocked',
        { cause: error },
      );
    }
  };

  return { getItem, setItem, removeItem };
}

export type UseStorageReturnValue<T> = [
  T, // current value
  (val: T | ((prevState: T) => T)) => void, // callback to set value in storage
  () => void, // callback to remove value from storage
];

export function createStorage<T>(type: StorageType) {
  const { getItem, setItem, removeItem } = createStorageHandler(type);

  return function useStorage({
    key,
    defaultValue,
    getInitialValueInEffect = true,
    sync = true,
    deserialize = deserializeJSON,
    serialize = (value: T) => serializeJSON(value),
  }: UseStorageOptions<T>): UseStorageReturnValue<T> {
    const readStorageValue = useCallback(
      (skipStorage?: boolean): T => {
        let storageBlockedOrSkipped;

        try {
          storageBlockedOrSkipped =
            typeof window === 'undefined' ||
            !(type in window) ||
            window[type] === null ||
            !!skipStorage;
        } catch (_e) {
          storageBlockedOrSkipped = true;
          console.warn('useLocalStorage: Failed to access storage, localStorage is blocked', {
            cause: _e,
          });
        }

        if (storageBlockedOrSkipped) {
          return defaultValue as T;
        }

        const storageValue = getItem(key);
        return storageValue !== null ? deserialize(storageValue) : (defaultValue as T);
      },
      [key, defaultValue],
    );

    const [value, setValue] = useState<T>(readStorageValue(getInitialValueInEffect));

    const setStorageValue = useCallback(
      (val: T | ((prevState: T) => T)) => {
        if (val instanceof Function) {
          setValue((current) => {
            const result = val(current);
            setItem(key, serialize(result));
            return result;
          });
        } else {
          setItem(key, serialize(val));
          setValue(val);
        }
      },
      [key],
    );

    const removeStorageValue = useCallback(() => {
      removeItem(key);
      setValue(defaultValue as T);
    }, [key, defaultValue]);

    useEffect(() => {
      if (sync) {
        const listener = (event: StorageEvent) => {
          if (event.storageArea === window[type] && event.key === key) {
            setValue(deserialize(event.newValue ?? undefined));
          }
        };
        window.addEventListener('storage', listener);
        return () => window.removeEventListener('storage', listener);
      }
      return () => {};
    }, [sync]);

    useEffect(() => {
      if (defaultValue !== undefined && value === undefined) {
        setStorageValue(defaultValue);
      }
    }, [defaultValue, value, setStorageValue]);

    useEffect(() => {
      const val = readStorageValue();
      val !== undefined && setStorageValue(val);
    }, [key]);

    return [value === undefined ? (defaultValue as T) : value, setStorageValue, removeStorageValue];
  };
}

export function readValue(type: StorageType) {
  const { getItem } = createStorageHandler(type);

  return function read<T>({
    key,
    defaultValue,
    deserialize = deserializeJSON,
  }: UseStorageOptions<T>) {
    let storageBlockedOrSkipped;

    try {
      storageBlockedOrSkipped =
        typeof window === 'undefined' || !(type in window) || window[type] === null;
    } catch (_e) {
      storageBlockedOrSkipped = true;
      console.warn('useLocalStorage: Failed to access storage, localStorage is blocked', {
        cause: _e,
      });
    }

    if (storageBlockedOrSkipped) {
      return defaultValue as T;
    }

    const storageValue = getItem(key);
    return storageValue !== null ? deserialize(storageValue) : (defaultValue as T);
  };
}
